import { Box, Button, Text } from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';

import Modal from '../../../../components/Modal';
import Input from '../../../../components/formComponents/Input';
import Select from '../../../../components/formComponents/Select';
import PrimaryButton from '../../../../components/ui/buttons/PrimaryButton';
import {
  OtherLienholder,
  OtherLienholderSlug,
  isTreatedAsOther,
} from '../../../../constants/lienholders';
import { Lienholder } from '../../../../gql/prs/types';
import { RudderEvent, rudderanalytics } from '../../../../services/analytics/rudderstack';
import {
  LienholderSearchOption,
  filterLienholderSearchOption,
  getLienholderSelectionError,
  resolveManualLienholderEntry,
  toLienholderSearchOptions,
} from './utils';

const KEYSTROKES_BEFORE_MANUAL_ENTRY = 6;

interface Props {
  licensePlateState?: string | null;
  isOpen: boolean;
  closeModal: () => void;
  lienholders: Lienholder[];
  handleSubmit: () => void;
  skipLienholderCheck?: boolean;
}

const LienholderModal: React.FC<Props> = ({
  licensePlateState,
  isOpen,
  closeModal,
  lienholders,
  handleSubmit,
  skipLienholderCheck = false,
}) => {
  const { values, setFieldValue } = useFormikContext<{
    lienholder: string;
    other_lienholder: string;
  }>();

  const [showManualEntry, setShowManualEntry] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [searchKeystrokes, setSearchKeystrokes] = useState(0);

  // Don't surface manual entry up front, it pushes customers to "Other". Only
  // offer it once they've typed enough to show they can't find their match.
  const canEnterManually = searchKeystrokes >= KEYSTROKES_BEFORE_MANUAL_ENTRY;

  useEffect(() => {
    if (!pendingSubmit) {
      return;
    }
    setPendingSubmit(false);
    handleSubmit();
  }, [pendingSubmit]);

  const lienholderOptions = useMemo(() => toLienholderSearchOptions(lienholders), [lienholders]);

  const onManualEntrySubmit = (typedValue: string) => {
    const resolution = resolveManualLienholderEntry(
      lienholders,
      typedValue,
      licensePlateState || null,
      skipLienholderCheck,
    );

    if (resolution.action === 'block') {
      rudderanalytics.track(RudderEvent.ManualLienholderAutoMatch, {
        typed_value: typedValue,
        outcome: 'blocked',
        reason: resolution.reason,
        lienholder_slug: resolution.lienholder.slug,
      });
      setError(resolution.message);
      return;
    }

    setError(null);

    if (resolution.action === 'map') {
      const matched = resolution.lienholder;
      rudderanalytics.track(RudderEvent.ManualLienholderAutoMatch, {
        typed_value: typedValue,
        outcome: 'matched',
        method: resolution.method,
        lienholder: matched.display_name,
        lienholder_slug: matched.slug,
        is_core_lienholder: matched.is_core,
      });
      setFieldValue('other_lienholder', '', false);
      setFieldValue('lienholder', matched.display_name, false);
      if (resolution.treatedAsOther) {
        rudderanalytics.track(RudderEvent.OtherLienholderSelected, {
          lienholder: matched.display_name,
          lienholder_slug: matched.slug,
          is_core_lienholder: matched.is_core,
        });
      }
      setPendingSubmit(true);
      return;
    }

    rudderanalytics.track(RudderEvent.ManualLienholderAutoMatch, {
      typed_value: typedValue,
      outcome: 'abstained',
      reason: resolution.reason,
      candidates: resolution.candidates,
    });
    setFieldValue('lienholder', OtherLienholder, false);
    rudderanalytics.track(RudderEvent.OtherLienholderSelected, {
      lienholder: typedValue,
      lienholder_slug: OtherLienholderSlug,
      is_core_lienholder: false,
    });
    setPendingSubmit(true);
  };

  const onSubmit = () => {
    if (showManualEntry) {
      const typedValue = values.other_lienholder?.trim();
      if (!typedValue) {
        setError('Please enter lienholder name');
        return;
      }
      onManualEntrySubmit(typedValue);
      return;
    }

    const selectedLh = lienholders.find((lh) => lh.display_name === values.lienholder);

    if (!selectedLh) {
      setError('Please select a lienholder');
      return;
    }

    const selectionError = getLienholderSelectionError(
      selectedLh,
      licensePlateState || null,
      skipLienholderCheck,
    );

    if (selectionError) {
      setError(selectionError.message);
      if (selectionError.reason === 'lienholder_disabled') {
        setFieldValue('lienholder', '');
      }
      return;
    }

    const treatedAsOther = isTreatedAsOther({
      lienholder: values.lienholder,
      lienholder_slug: selectedLh.slug,
      is_core: selectedLh.is_core,
    });

    if (treatedAsOther) {
      // Distinguish non-core lienholders treated as Other from manual entries.
      rudderanalytics.track(RudderEvent.OtherLienholderSelected, {
        lienholder: values.lienholder,
        lienholder_slug: selectedLh.slug,
        is_core_lienholder: selectedLh.is_core,
      });
    }
    setError(null);
    handleSubmit();
  };

  const onClose = () => {
    if (error) {
      setFieldValue('lienholder', '');
    }
    setError(null);
    setShowManualEntry(false);
    setSearchKeystrokes(0);
    closeModal();
  };

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      title="Let's find your lienholder."
      subtitle="Search for the bank or company your lease is through."
      size={{ base: 'md', md: '3xl' }}
    >
      <Box textAlign="center">
        {showManualEntry ? (
          <Box>
            <Input
              name="other_lienholder"
              placeholder="Who is your lease through?"
              _input={{
                // The modal is portaled out of the formik <form>, so Enter has no
                // form owner and would otherwise do nothing — route it through the
                // modal submit so it runs the auto-match like the button does.
                onKeyDown: (e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    onSubmit();
                  }
                },
              }}
            />
            <Button
              variant="link"
              mt={3}
              onClick={() => {
                setError(null);
                setShowManualEntry(false);
              }}
            >
              Back to search
            </Button>
          </Box>
        ) : (
          <Box>
            <Select<LienholderSearchOption>
              name="lienholder"
              placeholder="Search for your lienholder"
              options={lienholderOptions}
              filterOption={filterLienholderSearchOption}
              onInputChange={(_newValue, actionMeta) => {
                if (actionMeta.action === 'input-change') {
                  setSearchKeystrokes((count) => count + 1);
                }
              }}
              noOptionsMessage={() =>
                canEnterManually ? (
                  <Button
                    variant="link"
                    whiteSpace="normal"
                    // The menu unmounts on blur before a click would land, so keep
                    // the input from blurring on mousedown; onClick then fires for
                    // both mouse and keyboard (Enter/Space) activation.
                    onMouseDown={(e) => {
                      e.preventDefault();
                    }}
                    onClick={() => {
                      setError(null);
                      setShowManualEntry(true);
                    }}
                  >
                    Can't find your lienholder? Enter it manually
                  </Button>
                ) : (
                  "We couldn't find that lienholder."
                )
              }
            />
          </Box>
        )}
        {error && (
          <Text color="leaseEndRed" mt={4}>
            {error}
          </Text>
        )}
        <PrimaryButton mt={4} onClick={onSubmit} />
      </Box>
    </Modal>
  );
};

export default LienholderModal;
